import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPositionTrackerList } from '../../store/slices/positionTrackerSlice';
import { X, Loader2, CheckCircle, XCircle, Search, MapPin, ExternalLink, Sparkles } from 'lucide-react';

const SimpleStatusModal = ({ isOpen, onClose, brandName, affiliateUrl, trackingId }) => {
  const dispatch = useDispatch();
  const { crawlResult, crawlLoading, crawlError } = useSelector((state) => state.positionTracker);

  const [status, setStatus] = useState('searching'); // searching, found, analyzing, completed, error
  const [progress, setProgress] = useState(0);
  const [crawlData, setCrawlData] = useState(null);
  const [positionData, setPositionData] = useState(null);
  const [error, setError] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [canClose, setCanClose] = useState(false);
  const [positionSaved, setPositionSaved] = useState(false);

  const pollingIntervalRef = useRef(null);
  const timeIntervalRef = useRef(null);
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

  // Timer for elapsed time
  useEffect(() => {
    if (isOpen) {
      timeIntervalRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, [isOpen]);

  // Save position data when completed
  const savePositionData = async (positionResults) => {
    try {
      
      const response = await fetch(`${basePath}/api/position-tracker/savePosition`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trackingId: trackingId,
          brandName: brandName,
          affiliateUrl: affiliateUrl,
          positionResults: positionResults
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setPositionSaved(true);
        
        // Refresh the position tracker list to show updated data
        dispatch(fetchPositionTrackerList());
        
        return true;
      } else {
        console.error('Failed to save position data:', result.message);
        return false;
      }
    } catch (err) {
      console.error('Error saving position data:', err);
      return false;
    }
  };

  // Polling disabled - status tracking will be handled externally
  const pollStatus = async () => {
    // Status polling removed - external service will handle tracking
    // Modal will remain in "searching" state
  };

  // Listen to crawl results from Redux
  useEffect(() => {
    if (crawlResult && crawlResult.success) {
      setStatus('completed');
      setProgress(100);
      setCanClose(true);

      // Extract position data from crawl result
      if (crawlResult.data) {
        setPositionData({
          position: crawlResult.data.position,
          totalListings: crawlResult.data.totalListings,
          offerText: crawlResult.data.offerText,
          confidence: crawlResult.data.confidence
        });
        setPositionSaved(true);

        // Refresh the tracking list
        dispatch(fetchPositionTrackerList());
      }
    } else if (crawlError) {
      setStatus('error');
      // crawlError can be an object {message, suggestions, data} - extract the message string
      if (typeof crawlError === 'object' && crawlError !== null) {
        setError(crawlError.message || 'An unexpected error occurred');
      } else {
        setError(crawlError);
      }
      setCanClose(true);
    }
  }, [crawlResult, crawlError, dispatch]);

  // Monitor loading state
  useEffect(() => {
    if (crawlLoading) {
      setStatus('searching');
      setProgress(30);
    }
  }, [crawlLoading]);

  // Initialize modal state
  useEffect(() => {
    if (isOpen && trackingId) {
      setStatus('searching');
      setProgress(10);
      setElapsedTime(0);
      setError(null);
      setCanClose(false);
      setCrawlData(null);
      setPositionData(null);
      setPositionSaved(false);

    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [isOpen, trackingId]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    // Only allow closing if completed or error
    if (status !== 'completed' && status !== 'error') {
      return; // Don't allow closing while in progress
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (timeIntervalRef.current) {
      clearInterval(timeIntervalRef.current);
    }

    // Refresh the list one more time when closing (in case user closes during polling)
    if (status === 'completed' && positionSaved) {
      dispatch(fetchPositionTrackerList());
    }

    onClose();
  };

  if (!isOpen) return null;

  // Prevent closing by clicking outside during analysis
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && (status === 'completed' || status === 'error')) {
      handleClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white">Position Tracking Progress</h2>
            <p className="text-blue-100 text-sm mt-1">Searching for "{brandName}" on {affiliateUrl}</p>
          </div>
          {/* Only show close button when completed or error */}
          {(status === 'completed' || status === 'error') && (
            <button
              onClick={handleClose}
              className="text-white rounded-lg p-2 hover:bg-white hover:bg-opacity-20 cursor-pointer transition-all"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-sm text-gray-600 dark:text-slate-400">{progress}% Complete</span>
            <span className="text-sm text-gray-600 dark:text-slate-400">
              {status === 'searching' && 'Searching...'}
              {status === 'found' && 'Brand Found!'}
              {status === 'analyzing' && 'Finding Position...'}
              {status === 'completed' && 'Complete'}
              {status === 'error' && 'Error'}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">

          {/* Searching/Analyzing States - Simplified */}
          {(status === 'searching' || status === 'found' || status === 'analyzing') && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="mb-6">
                <Loader2 className="w-16 h-16 text-blue-500 dark:text-blue-400 animate-spin" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">
                Analyzing Position
              </h3>
              <p className="text-gray-600 dark:text-slate-400 text-center max-w-md">
                Please wait while we detect the position of "{brandName}" on the affiliate website...
              </p>
              <div className="mt-6 w-full max-w-xs">
                <div className="flex justify-between text-sm text-gray-500 dark:text-slate-400 mb-2">
                  <span>Progress</span>
                  <span>{progress}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Completed State */}
          {status === 'completed' && (
            <div className="space-y-4">
              {/* Success Header */}
              <div className="text-center mb-4">
                <CheckCircle className="w-12 h-12 text-green-500 dark:text-green-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Analysis Complete!</h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                  Position tracking completed for "{brandName}"
                </p>
              </div>

              {/* Position Results Card */}
              {positionData && positionData.position && (
                <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl shadow-md overflow-hidden">
                  {/* Position Section */}
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm font-medium mb-1">Current Position</p>
                        <div className="flex items-baseline gap-2">
                          <p className="text-white text-5xl font-bold">#{positionData.position}</p>
                          {positionData.totalListings && (
                            <p className="text-blue-200 text-lg font-medium">
                              of {positionData.totalListings}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="bg-white bg-opacity-20 rounded-xl p-3">
                        <MapPin className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Brand & Site Info */}
                  <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1">Brand</p>
                        <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">{brandName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1">Affiliate Site</p>
                        <a
                          href={affiliateUrl?.startsWith('http') ? affiliateUrl : `https://${affiliateUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        >
                          {affiliateUrl?.replace(/^https?:\/\/(?:www\.)?/, '').replace(/\/$/, '')}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Bonus Offer Section */}
                  {positionData.offerText && (
                    <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                      <div className="flex items-start gap-3">
                        <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs text-emerald-700 dark:text-emerald-400 uppercase tracking-wide mb-1">Current Offer</p>
                          <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                            {positionData.offerText}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* No Position Found */}
              {positionData && !positionData.position && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-start gap-3">
                  <Search className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800 dark:text-yellow-300">Position Not Found</p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                      "{brandName}" could not be located in the rankings on this affiliate site.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-red-800 dark:text-red-300">Error Occurred</p>
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    {error || 'An unexpected error occurred during the search'}
                  </p>
                </div>
              </div>

              {crawlData && crawlData.visitedCount > 0 && (
                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    Searched {crawlData.visitedCount} pages before encountering an issue.
                  </p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-slate-800 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-slate-700">
          <div className="text-sm text-gray-600 dark:text-slate-400">
            {status === 'completed' ? (
              <span className="text-green-600 dark:text-green-400 font-medium flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Tracking complete
              </span>
            ) : status === 'error' ? (
              <span className="text-red-600 dark:text-red-400 font-medium flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Tracking failed
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Please wait, analysis in progress...
              </span>
            )}
          </div>
          <button
            onClick={handleClose}
            disabled={status !== 'completed' && status !== 'error'}
            className={`px-6 py-2 rounded-lg font-medium transition-all ${
              status === 'completed'
                ? 'bg-green-600 text-white hover:bg-green-700'
                : status === 'error'
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
            }`}
            title={status !== 'completed' && status !== 'error' ? 'Please wait until analysis completes' : 'Close dialog'}
          >
            {status === 'completed' || status === 'error' ? 'Close' : 'Please Wait...'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleStatusModal;